import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Search, ChevronDown, MessageCircle, Headset, Phone, Mail, Clock, FileText } from "lucide-react";

function Faq({ f }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#f0f1f3] last:border-0">
      <button data-testid={`faq-${f.id}`} onClick={() => setOpen(!open)} className="flex w-full items-center justify-between gap-3 py-3 text-left text-sm font-medium text-[#161d23] hover:text-[var(--brand)]">
        {f.q} <ChevronDown size={16} className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <p data-testid={`faq-answer-${f.id}`} className="pb-3 text-sm text-[#4a4f54]">{f.a}</p>}
    </div>
  );
}

function HelpCard({ icon: Icon, title, sub, cta, onClick, testid, dark }) {
  return (
    <div className={`flex flex-col gap-3 rounded-xl p-5 shadow-sm ${dark ? "bg-[#161d23] text-white" : "border border-[#e5e9f0] bg-white"}`}>
      <div className={`flex h-10 w-10 items-center justify-center rounded-full ${dark ? "bg-[var(--brand)]" : "bg-[#161d23] text-white"}`}><Icon size={18} /></div>
      <div><div className="font-semibold">{title}</div><div className={`text-sm ${dark ? "text-white/70" : "text-[#6a6d70]"}`}>{sub}</div></div>
      <button data-testid={testid} onClick={onClick} className="mt-auto self-start rounded-full bg-[var(--brand)] px-4 py-2 text-sm font-semibold text-white hover:bg-[var(--brand-hover)]">{cta}</button>
    </div>
  );
}

export function BqSupport({ openRob, talkToHuman, brand }) {
  const bot = brand.botName || "Rob";
  const [faqs, setFaqs] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/shop/support/faqs").then((r) => setFaqs(r.data.faqs)).catch(() => {}); }, []);
  const groups = useMemo(() => {
    const s = q.toLowerCase();
    const hits = faqs.filter((f) => !s || `${f.q} ${f.a}`.toLowerCase().includes(s));
    return hits.reduce((acc, f) => ({ ...acc, [f.cat]: [...(acc[f.cat] || []), f] }), {});
  }, [faqs, q]);
  return (
    <div data-testid="shop-support" className="space-y-6">
      <div className="rounded-2xl bg-[#161d23] px-6 py-8 sm:px-10">
        <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[var(--brand)]">Help centre</span>
        <h1 className="mt-1 text-2xl font-extrabold text-white sm:text-3xl">How can we help with your {brand.resale ? "" : "GNFR "}order?</h1>
        <div className="relative mt-4 max-w-xl">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#89919a]" />
          <input data-testid="faq-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search help: cut-off, returns, approvals…" className="w-full rounded-full border-0 py-3 pl-10 pr-4 text-sm outline-none" />
        </div>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <HelpCard dark icon={MessageCircle} title={`Ask ${bot}`} sub="Instant answers on orders, deliveries, products and returns, 24/7." cta={`Chat with ${bot}`} onClick={openRob} testid="support-ask-rob" />
        <HelpCard icon={Headset} title="Talk to a person" sub={`Live chat with the EP Group ${brand.resale ? "webshop" : "GNFR"} team, Mon–Fri 08:00–18:00.`} cta="Start live chat" onClick={talkToHuman} testid="support-talk-human" />
      </div>
      <div className="grid gap-5 lg:grid-cols-[2fr_1fr]">
        <div className="rounded-xl border border-[#e5e9f0] bg-white p-5 shadow-sm" data-testid="faq-list">
          <div className="mb-2 font-semibold text-[#161d23]">Frequently asked questions</div>
          {Object.keys(groups).length === 0 && <p className="py-6 text-sm text-[#6a6d70]">No matches. Try Ask {bot} instead.</p>}
          {Object.entries(groups).map(([cat, list]) => (
            <div key={cat} className="mt-3"><div className="text-[11px] font-semibold uppercase tracking-wider text-[var(--brand)]">{cat}</div>{list.map((f) => <Faq key={f.id} f={f} />)}</div>
          ))}
        </div>
        <div className="space-y-3 rounded-xl border border-[#e5e9f0] bg-white p-5 text-sm shadow-sm">
          <div className="font-semibold text-[#161d23]">Other ways to reach us</div>
          <div className="flex items-center gap-2 text-[#32363a]"><Mail size={15} className="text-[var(--brand)]" /> {brand.resale ? "webshop.support" : "gnfr.support"}@epgroup.co.uk</div>
          <div className="flex items-center gap-2 text-[#32363a]"><Phone size={15} className="text-[var(--brand)]" /> +44 (0)121 706 6181</div>
          <div className="flex items-center gap-2 text-[#32363a]"><Clock size={15} className="text-[var(--brand)]" /> Mon–Fri, 08:00–18:00</div>
          <a data-testid="support-user-guide" href={brand.guide} target="_blank" rel="noreferrer" className="flex items-center gap-2 font-semibold text-[var(--brand)] hover:underline"><FileText size={15} /> Download the user guide (PDF)</a>
          {brand.quickStart && <a data-testid="support-quick-start" href={`${process.env.REACT_APP_BACKEND_URL}/api/shop/quick-start.pdf`} target="_blank" rel="noreferrer" className="flex items-center gap-2 font-semibold text-[var(--brand)] hover:underline"><FileText size={15} /> Print the staff-room quick start card</a>}
        </div>
      </div>
    </div>
  );
}
